import React from 'react';
import { Form } from 'antd';
import PropsType from 'prop-types';
import { useNavigate } from 'react-router-dom';

import InputComponent from '@/components/Input';
import ButtonComponent from '@/components/Button';
import { validateEmail } from '@/utils/validate';
import CheckboxInput from '@/components/Checkbox';
import { ENV, ROUTES } from '@/constants';

import './index.scss';
import { useTranslation } from 'react-i18next';
import { getCurrentLanguage } from '@/utils/locale';

function LoginContainer({ onSubmit, isLoading }) {
  const { t: translation } = useTranslation();
  const navigate = useNavigate();
  const listItem = [
    translation('login.banner.1'),
    translation('login.banner.2'),
    translation('login.banner.3'),
    translation('login.banner.4'),
    translation('login.banner.5'),
    translation('login.banner.6'),
  ];

  const renderItem = (key, item) => (
    <div key={key} className="login-page__item">
      <span className="check-icon" />
      <p className="mb-0">{item}</p>
    </div>
  );

  const handleSignIn = (e) => {
    onSubmit(e);
  };

  const openLink = (path) => {
    navigate(path);
  };

  const lang = getCurrentLanguage();
  const websiteURLBaseOnLanguage =
    lang === 'th' ? ENV.WEBSITE_URL : `${ENV.WEBSITE_URL}/${lang}`;

  return (
    <div className="login-page">
      <div className="login-page__container">
        <div className="login-page__left">
          <div className="logo-wrapper">
            <a href={websiteURLBaseOnLanguage}>
              <div className="logo" />
            </a>
          </div>
          <div className="login-page__title">{translation('login.title')}</div>
          <div className="login-page__left__group">
            {listItem.map((item, key) => renderItem(key, item))}
          </div>
        </div>
        <div className="login-page__right">
          <div className="login-page__right__form">
            <h1 className="login-page__right__title">
              {translation('login.form.welcome')}
            </h1>
            <span className="login-page__right__desc">
              {translation('login.form.welcome_back')}
            </span>
            <Form
              className="login-page__right__form__container"
              name="signIn-form"
              layout="vertical"
              onFinish={handleSignIn}
              autoComplete="off"
            >
              <Form.Item
                name="email"
                rules={[
                  {
                    required: true,
                    message: translation('login.form.errors.email.required'),
                  },
                  () => ({
                    validator(_, value) {
                      if (value && !validateEmail(value)) {
                        return Promise.reject(
                          new Error(
                            translation('login.form.errors.email.not_valid'),
                          ),
                        );
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <InputComponent
                  placeholder={translation('login.form.placeholder.email')}
                  label={translation('login.form.fields.email')}
                />
              </Form.Item>
              <Form.Item
                name="password"
                rules={[
                  {
                    required: true,
                    message: translation('login.form.errors.password.required'),
                  },
                ]}
              >
                <InputComponent
                  placeholder={translation('login.form.placeholder.password')}
                  label={translation('login.form.fields.password')}
                  type="password"
                />
              </Form.Item>
              <div className="d-flex justify-content-between login-page__checkbox">
                <CheckboxInput>
                  <span className="login-page__term">
                    {translation('login.form.fields.remember_me')}
                  </span>
                </CheckboxInput>
                <span
                  className="login-page__right__desc --link"
                  aria-hidden="true"
                  onClick={() => openLink(ROUTES.FORGOT_PASSWORD)}
                >
                  {translation('login.forgot_password')}
                </span>
              </div>

              <Form.Item className="login-page__submit-btn">
                <ButtonComponent
                  loading={isLoading}
                  type="primary"
                  htmlType="submit"
                >
                  {translation('sign_up.signin_link')}
                </ButtonComponent>
              </Form.Item>
              <div className="login-page__right__desc text-center">
                {translation('login.no_account_yet')}
                <span
                  className="login-page__right__desc --link"
                  aria-hidden="true"
                  onClick={() => openLink(ROUTES.SIGNUP_PATH)}
                >
                  {translation('login.register_text')}
                </span>
              </div>
            </Form>
          </div>
        </div>
      </div>
    </div>
  );
}

LoginContainer.propTypes = {
  onSubmit: PropsType.func,
  isLoading: PropsType.bool,
};

export default LoginContainer;
